import React, { useState } from 'react';
import { CheckCircle2Icon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { DailyProgress } from '../components/queue/DailyProgress';
import { LeadWorkspace } from '../components/queue/LeadWorkspace';
import { ActivityTimeline } from '../components/queue/ActivityTimeline';
import { QueuePanel } from '../components/queue/QueuePanel';
import { EmailPanel } from '../components/queue/EmailPanel';
import { CallDialog } from '../components/queue/CallDialog';
import { useAppState } from '../contexts/AppState';
import { requiredCallsFor } from '../utils/format';
interface WorkQueueProps {
  layout?: 'split' | 'focused';
}
export function WorkQueue({
  layout = 'split'
}: WorkQueueProps) {
  const {
    queue,
    deferredIds,
    currentLead,
    publishedSources,
    publishedPriority,
    nextLead,
    canAdvance,
    logCall,
    logSms,
    logEmail,
    leadsWorkedToday,
    callsCompletedToday
  } = useAppState();
  const [emailOpen, setEmailOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const enabledPriority = publishedPriority.filter(r => r.enabled);
  const overdueCount = queue.reduce((sum, lead) => sum + lead.activities.filter(a => a.dueInMinutes !== undefined && a.dueInMinutes < 0).length, 0);
  const source = currentLead ? publishedSources.find(s => s.id === currentLead.sourceId) : undefined;
  const priorityRule = currentLead ? enabledPriority.find(r => r.id === currentLead.priorityRuleId) : undefined;
  const priorityIndex = currentLead ? enabledPriority.findIndex(r => r.id === currentLead.priorityRuleId) + 1 : 0;
  return <div className="space-y-5" data-id="element-492" data-mp="38qc75hku15ds">
      <div data-id="element-493" data-mp="3drjifqm6omp7">
        <h1 className="text-lg font-semibold tracking-tight text-ink-900" data-id="element-494" data-mp="3dq8ftu4h5kzo">Today&apos;s Work Queue</h1>
        <p className="mt-0.5 text-sm text-ink-500" data-id="element-495" data-mp="1efh2urgokcgt">
          Leads are presented in the exact order set by management. Work the current lead, then
          advance.
        </p>
      </div>

      <DailyProgress remaining={queue.length} workedToday={leadsWorkedToday} callsCompleted={callsCompletedToday} overdueActivities={overdueCount} total={queue.length + leadsWorkedToday} data-id="element-496" data-mp="1bdm1hjubx951" />

      <div className={layout === 'focused' ? 'space-y-5' : 'grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]'} data-id="element-497" data-mp="1lc1q9g5vw6bd">
        <div className="space-y-5" data-id="element-498" data-mp="1u9u92uatdw3i">
          {currentLead ? <>
              <LeadWorkspace lead={currentLead} source={source} priorityIndex={priorityIndex} priorityLabel={priorityRule?.label ?? 'Cadence Lead'} priorityReason={priorityRule?.summary ?? ''} requiredCalls={requiredCallsFor(source, currentLead.ageMinutes)} ownerName="Sarah Johnson" canAdvance={canAdvance} onCall={() => setCallOpen(true)} onSms={() => logSms(currentLead.id)} onEmail={() => setEmailOpen(true)} onNext={nextLead} data-id="element-499" data-mp="1pbanww41ooc7" />
              <ActivityTimeline activities={currentLead.activities} data-id="element-500" data-mp="2wllwglqamycu" />
            </> : <Card className="flex flex-col items-center justify-center px-6 py-20 text-center" data-id="element-501" data-mp="2iq1dxreh8i5y">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700" data-id="element-502" data-mp="2np4jre2a6sez">
                <CheckCircle2Icon className="h-6 w-6" aria-hidden="true" data-id="element-503" data-mp="2dou7bvo2lp0v" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-ink-900" data-id="element-504" data-mp="1zxfnxnq93lta">Queue complete</h2>
              <p className="mt-1 max-w-sm text-sm text-ink-500" data-id="element-505" data-mp="1zvmdrygfijgj">
                Every lead has met today&apos;s required cadence. New leads will appear here
                automatically as they arrive.
              </p>
            </Card>}
        </div>

        <QueuePanel upcoming={queue.filter(lead => lead.id !== currentLead?.id)} currentLead={currentLead} deferredIds={deferredIds} sources={publishedSources} priority={publishedPriority} className={layout === 'focused' ? '' : 'xl:sticky xl:top-[88px] xl:max-h-[calc(100vh-120px)]'} data-id="element-506" data-mp="1ux1ga6skadvs" />
      </div>

      {currentLead ? <>
          <EmailPanel open={emailOpen} lead={currentLead} onClose={() => setEmailOpen(false)} onSend={templateName => {
        logEmail(currentLead.id, templateName);
        setEmailOpen(false);
      }} data-id="element-507" data-mp="2jjufvlxqp4i9" />
          <CallDialog open={callOpen} lead={currentLead} onClose={() => setCallOpen(false)} onLog={outcome => {
        logCall(currentLead.id, outcome);
        setCallOpen(false);
      }} data-id="element-508" data-mp="25ldz1265xxme" />
        </> : null}
    </div>;
}